import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../config';
import VedicChart from '../components/VedicChart';

/**
 * AstroLLM — Sprint 1
 * Three-panel "Digital Astrolabe": Archive | Oracle | Dashboard
 *
 * SCIENCE GUARDRAILS
 *  - Profiles list = user's saved birth profiles (no fake data)
 *  - Chart computed by our in-house Swiss Ephemeris on profile load
 *  - AI chat hits /api/v3/ai-llm/chat which:
 *      - re-computes the chart server-side (real math)
 *      - retrieves RAG chunks from classical-text vector index
 *      - tells the AI to cite [n] when using a chunk
 *      - rejects fabrication via system prompt
 *  - Cited sources displayed below each AI message — actual classical-text refs
 *  - Right panel auto-renders the chart layer the AI is discussing
 */
function AstroLLMPage({ onBack, onSignOut }) {
  // -------- state --------
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [chart, setChart] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  const [messages, setMessages] = useState([]);    // { role, content, sources?, highlight?, model?, tokens?, cost? }
  const [input, setInput] = useState('');
  const [model, setModel] = useState('deepseek');
  const [tool, setTool] = useState('general');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const [rightPanelTab, setRightPanelTab] = useState('rasi');  // rasi | dashas | yogas | planets | shadbala
  const chatScrollRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('astrogem_token') : null;

  const authedFetch = (path, opts = {}) =>
    fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...(opts.headers || {}), Authorization: 'Bearer ' + token },
    }).then(r => r.json());

  // -------- load profiles on mount --------
  useEffect(() => {
    if (!token) return;
    authedFetch('/api/v3/user/profiles?limit=100').then(r => {
      if (r.success) {
        setProfiles(r.profiles || []);
        if (r.profiles?.length && !activeProfileId) {
          setActiveProfileId(r.profiles[0].id);
        }
      }
    });
  }, [token]); // eslint-disable-line

  // -------- load chart when active profile changes --------
  useEffect(() => {
    if (!activeProfileId || !token) return;
    setChartLoading(true);
    setChart(null);
    setMessages([]);
    authedFetch(`/api/v3/ai-llm/chart/${activeProfileId}`)
      .then(r => {
        if (r.success) setChart(r.chart);
        else setError(r.error || 'Could not load chart');
      })
      .catch(e => setError(e.message))
      .finally(() => setChartLoading(false));
  }, [activeProfileId, token]); // eslint-disable-line

  // Auto-scroll chat
  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [messages, sending]);

  // -------- handlers --------
  const sendMessage = async (overrideMessage = null) => {
    const text = (overrideMessage ?? input).trim();
    if (!text || sending || !activeProfileId) return;
    const newUserMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, newUserMsg]);
    setInput('');
    setSending(true);
    setError('');

    const r = await authedFetch('/api/v3/ai-llm/chat', {
      method: 'POST',
      body: JSON.stringify({
        profile_id: activeProfileId,
        message: text,
        history: messages.slice(-6),
        model,
        tool,
      }),
    });

    setSending(false);

    if (!r.success) {
      setMessages(prev => [...prev, {
        role: 'assistant', content: '⚠️ ' + (r.error || 'Could not generate response'),
        error: true,
      }]);
      return;
    }

    setMessages(prev => [...prev, {
      role: 'assistant',
      content: r.answer,
      sources: r.sources || [],
      highlight: r.highlight,
      model: r.model,
      tool: r.tool,
      tokens: r.tokens,
      cost: r.cost_usd,
      billing: r.billing,
    }]);

    // Auto-switch right panel tab if AI hinted at a specific chart layer
    if (r.highlight?.chart_type) {
      const tabMap = {
        rasi: 'rasi', navamsa: 'rasi', dashas: 'dashas', yogas: 'yogas',
        shadbala: 'shadbala', dasamsa: 'rasi', dwadasamsa: 'rasi',
      };
      const newTab = tabMap[r.highlight.chart_type] || 'rasi';
      setRightPanelTab(newTab);
    }
  };

  const activeProfile = useMemo(
    () => profiles.find(p => String(p.id) === String(activeProfileId)),
    [profiles, activeProfileId]
  );

  const quickActions = [
    { label: 'Analyze my Rajayogas', tool: 'yoga', prompt: 'What Raja Yogas and Panchamahapurusha Yogas are present in this chart, and how do they manifest?' },
    { label: 'Current Dasha breakdown', tool: 'dasha', prompt: 'Explain my current Mahadasha and Antardasha — what themes are most active right now?' },
    { label: 'Lal Kitab remedies', tool: 'lalkitab', prompt: 'What Lal Kitab remedies (upayas) would be appropriate based on this chart?' },
    { label: 'Chart strengths', tool: 'general', prompt: 'What are the strongest features of this chart? Focus on dignified, well-placed planets and beneficial combinations.' },
    { label: 'Areas to be mindful of', tool: 'general', prompt: 'What underlying themes in this chart warrant conscious attention? Use measured language.' },
  ];

  // -------- render --------
  if (!token) {
    return <Centered>Sign in to use AstroLLM.</Centered>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white">
      {/* Top bar */}
      <nav className="border-b border-slate-800 bg-slate-900/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 py-2.5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-white/60 hover:text-white text-sm">← Back</button>
            <span className="text-2xl">🪔</span>
            <span className="text-lg font-bold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
              AstroLLM
            </span>
            <span className="text-[10px] uppercase tracking-wider text-amber-300/60 ml-1">Sprint 1</span>
          </div>
          <div className="text-xs text-white/40">
            Grounded in: Swiss Ephemeris · BPHS · Phala Deepika · Lal Kitab · Komilla Sutton · Marc Boney
          </div>
        </div>
      </nav>

      <main className="max-w-[1600px] mx-auto p-3 grid grid-cols-12 gap-3 h-[calc(100vh-3.5rem)]">
        {/* LEFT — Archive */}
        <aside className="col-span-12 lg:col-span-3 xl:col-span-2 bg-slate-900/50 border border-slate-800 rounded-xl p-3 overflow-y-auto">
          <h3 className="text-xs font-bold uppercase tracking-wider text-white/50 mb-2">📋 Profiles</h3>
          {profiles.length === 0 ? (
            <p className="text-white/40 text-sm">
              No saved profiles yet. Generate a chart first to save one.
            </p>
          ) : (
            <div className="space-y-1.5">
              {profiles.map(p => (
                <button
                  key={p.id}
                  onClick={() => setActiveProfileId(p.id)}
                  className={`w-full text-left p-2 rounded text-sm transition ${String(activeProfileId) === String(p.id) ? 'bg-purple-600/30 border border-purple-500' : 'hover:bg-slate-800 border border-transparent'}`}
                >
                  <div className="font-bold truncate">{p.name}</div>
                  {p.relationship && <div className="text-[10px] text-white/50 uppercase">{p.relationship}</div>}
                  <div className="text-[10px] text-white/40">{p.date} · {p.time}</div>
                  {p.location_label && <div className="text-[10px] text-white/40 truncate">{p.location_label}</div>}
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* CENTER — Oracle (chat) */}
        <section className="col-span-12 lg:col-span-6 xl:col-span-7 bg-slate-900/50 border border-slate-800 rounded-xl flex flex-col">
          {/* chat header */}
          <div className="p-3 border-b border-slate-800 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm font-bold">
                {activeProfile ? `Reading: ${activeProfile.name}` : 'Pick a profile to start'}
              </div>
              {activeProfile && (
                <div className="text-[11px] text-white/50">
                  {activeProfile.date} · {activeProfile.time} · {activeProfile.location_label || `${activeProfile.latitude},${activeProfile.longitude}`}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Model selector */}
              <label className="text-[10px] uppercase text-white/40">Model:</label>
              <select value={model} onChange={e => setModel(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs">
                <option value="deepseek">DeepSeek (active)</option>
                <option value="openai">OpenAI GPT-4o (soon)</option>
                <option value="claude">Claude Sonnet (soon)</option>
              </select>
              {/* Tool/lens selector */}
              <label className="text-[10px] uppercase text-white/40">Lens:</label>
              <select value={tool} onChange={e => setTool(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs">
                <option value="general">General</option>
                <option value="yoga">Yoga Detector</option>
                <option value="dasha">Dasha / Timeline</option>
                <option value="lalkitab">Lal Kitab</option>
                <option value="remedies">Remedies</option>
                <option value="transit">Transits</option>
              </select>
            </div>
          </div>

          {/* messages */}
          <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {chartLoading && (
              <div className="text-center text-white/50 text-sm py-6">⏳ Computing chart…</div>
            )}
            {!chartLoading && messages.length === 0 && activeProfile && (
              <div className="text-white/50 text-sm text-center py-10">
                <div className="text-3xl mb-2">🪔</div>
                Ask any question about <strong>{activeProfile.name}</strong>'s chart.<br />
                Try a Quick Action below, or type your own question.
              </div>
            )}
            {messages.map((m, i) => (
              <ChatMessage key={i} msg={m} />
            ))}
            {sending && (
              <div className="text-white/50 text-sm italic">🪔 AstroLLM is consulting the sources…</div>
            )}
            {error && (
              <div className="bg-red-500/10 border border-red-500/40 rounded p-3 text-sm text-red-200">
                ⚠️ {error}
              </div>
            )}
          </div>

          {/* Quick actions */}
          {activeProfile && (
            <div className="px-3 pt-2 border-t border-slate-800">
              <div className="flex flex-wrap gap-1.5">
                {quickActions.map((qa, i) => (
                  <button
                    key={i}
                    disabled={sending}
                    onClick={() => { setTool(qa.tool); sendMessage(qa.prompt); }}
                    className="text-xs px-2.5 py-1 bg-purple-600/20 hover:bg-purple-600/40 border border-purple-500/40 rounded disabled:opacity-50 transition"
                  >
                    {qa.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* input */}
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="p-3 border-t border-slate-800 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!activeProfile || sending}
              placeholder={activeProfile ? `Ask about ${activeProfile.name}'s chart…` : 'Select a profile first…'}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-purple-400 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!activeProfile || sending || !input.trim()}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-5 py-2 rounded-lg font-bold text-sm disabled:opacity-50 transition"
            >
              Ask
            </button>
          </form>
        </section>

        {/* RIGHT — Dashboard */}
        <aside className="col-span-12 lg:col-span-3 xl:col-span-3 bg-slate-900/50 border border-slate-800 rounded-xl flex flex-col overflow-hidden">
          <div className="px-3 pt-3 border-b border-slate-800">
            <h3 className="text-xs font-bold uppercase tracking-wider text-white/50 mb-2">📊 Dashboard</h3>
            <div className="flex gap-1 -mb-px overflow-x-auto">
              {[
                ['rasi', 'Chart'],
                ['planets', 'Planets'],
                ['dashas', 'Dashas'],
                ['yogas', 'Yogas'],
                ['shadbala', 'Shadbala'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setRightPanelTab(id)}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1.5 border-b-2 whitespace-nowrap transition ${rightPanelTab === id ? 'border-purple-400 text-white' : 'border-transparent text-white/50 hover:text-white'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 text-xs">
            {!chart && <p className="text-white/40">Select a profile…</p>}
            {chart && rightPanelTab === 'rasi' && <RasiPanel chart={chart} />}
            {chart && rightPanelTab === 'planets' && <PlanetsPanel chart={chart} />}
            {chart && rightPanelTab === 'dashas' && <DashasPanel chart={chart} />}
            {chart && rightPanelTab === 'yogas' && <YogasPanel chart={chart} />}
            {chart && rightPanelTab === 'shadbala' && <p className="text-white/40 italic">Shadbala visualization — Sprint 4. Backend has data; UI rendering coming.</p>}
          </div>
        </aside>
      </main>
    </div>
  );
}

// =============== sub-components ===============

function ChatMessage({ msg }) {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-purple-600 text-white rounded-lg px-3 py-2 text-sm">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start max-w-[92%]">
      <div className={`w-full rounded-lg px-4 py-3 text-sm ${msg.error ? 'bg-red-500/20 border border-red-500/40' : 'bg-slate-800 border border-slate-700'}`}>
        <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-purple-300">
          🪔 AstroLLM
          {msg.tool && msg.tool !== 'general' && (
            <span className="bg-purple-600/30 px-1.5 py-0.5 rounded text-[10px] uppercase">{msg.tool}</span>
          )}
          {msg.model && <span className="text-white/40 text-[10px] ml-auto">{msg.model}</span>}
        </div>
        <FormattedText content={msg.content} />
        {msg.sources?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-700">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-purple-300 mb-1">
              📚 Sources cited ({msg.sources.length})
            </p>
            <div className="space-y-1">
              {msg.sources.map(s => (
                <div key={s.index} className="text-[10px] bg-purple-900/30 rounded px-2 py-1">
                  <span className="text-purple-300 font-bold">[{s.index}] {s.source}:</span>{' '}
                  <span className="text-white/70 italic">{s.snippet}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(msg.tokens || msg.cost) && (
          <div className="mt-2 text-[9px] text-white/30">
            {msg.tokens} tok · ${msg.cost?.toFixed(5) || '0.00000'}{msg.billing?.source && ` · ${msg.billing.source}`}
          </div>
        )}
      </div>
    </div>
  );
}

function FormattedText({ content }) {
  if (!content) return null;
  // Minimal: split on lines, render **bold** inline, treat ॐ on its own line as flourish
  const lines = String(content).split(/\r?\n/);
  return (
    <div className="space-y-2 text-white/90 leading-relaxed">
      {lines.map((line, i) => {
        const t = line.trim();
        if (!t) return null;
        if (t === 'ॐ' || t === '🕉') {
          return <div key={i} className="text-center text-amber-300 text-xl pt-1">ॐ</div>;
        }
        const heading = t.match(/^\*\*(.+?)\*\*\s*$/);
        if (heading) {
          return (
            <div key={i} className="text-[11px] font-bold uppercase tracking-wider text-purple-300 pt-1">
              {heading[1]}
            </div>
          );
        }
        return (
          <p key={i}>
            {t.split(/(\*\*[^*]+\*\*)/g).map((chunk, j) =>
              chunk.startsWith('**') && chunk.endsWith('**')
                ? <strong key={j}>{chunk.slice(2, -2)}</strong>
                : <span key={j}>{chunk}</span>
            )}
          </p>
        );
      })}
    </div>
  );
}

function RasiPanel({ chart }) {
  // Reuse VedicChart which takes chartData with .planets + .houses
  return (
    <div>
      <div className="text-white/60 mb-2 text-[11px]">
        Asc: <strong className="text-white">{chart.ascendant?.sign} {chart.ascendant?.degree?.toFixed?.(1)}°</strong>
        {' · '}Ayanamsha: {chart.ayanamsha?.name || 'Lahiri'}
      </div>
      <div className="bg-slate-950 rounded p-1">
        <VedicChart chartData={{ planets: chart.planets, houses: chart.houses, ascendant: chart.ascendant }} />
      </div>
    </div>
  );
}

function PlanetsPanel({ chart }) {
  const planets = Object.entries(chart.planets || {});
  return (
    <table className="w-full text-[11px]">
      <thead className="text-white/40 uppercase text-[9px]">
        <tr>
          <th className="text-left py-1">Planet</th>
          <th className="text-left">Sign</th>
          <th className="text-right">Deg</th>
          <th className="text-left">Naks</th>
        </tr>
      </thead>
      <tbody>
        {planets.map(([name, p]) => (
          <tr key={name} className="border-t border-slate-800">
            <td className="py-1 font-bold">{name}</td>
            <td>{p.sign}{p.isRetrograde && ' R'}</td>
            <td className="text-right">{p.degree}°{p.minute || 0}'</td>
            <td className="text-white/60">{p.nakshatra?.name?.slice(0, 8) || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DashasPanel({ chart }) {
  const current = chart.dashas?.current;
  if (!current) return <p className="text-white/40 italic">No dasha data computed.</p>;
  const adCurrent = current.antardashas?.find?.(a => a.isCurrent);
  return (
    <div className="space-y-2">
      <div className="bg-purple-600/20 border border-purple-500/40 rounded p-2">
        <div className="text-[10px] uppercase text-purple-300 mb-1">Current Mahadasha</div>
        <div className="font-bold">{current.lord}</div>
        <div className="text-[10px] text-white/50">{current.startDate} → {current.endDate}</div>
      </div>
      {adCurrent && (
        <div className="bg-pink-600/20 border border-pink-500/40 rounded p-2">
          <div className="text-[10px] uppercase text-pink-300 mb-1">Current Antardasha</div>
          <div className="font-bold">{adCurrent.lord}</div>
          <div className="text-[10px] text-white/50">{adCurrent.startDate} → {adCurrent.endDate}</div>
        </div>
      )}
      <div className="pt-2 text-[10px] text-white/50">Upcoming antardashas in this Mahadasha:</div>
      {(current.antardashas || []).slice(0, 9).map(a => (
        <div key={a.lord + a.startDate} className={`text-[10px] py-1 px-2 rounded ${a.isCurrent ? 'bg-pink-600/20' : 'hover:bg-slate-800'}`}>
          <strong>{a.lord}</strong>: {a.startDate} → {a.endDate}
        </div>
      ))}
    </div>
  );
}

function YogasPanel({ chart }) {
  const yogas = chart.yogas || [];
  if (yogas.length === 0) {
    return <p className="text-white/40 italic">No yogas detected by the engine for this chart.</p>;
  }
  return (
    <div className="space-y-1.5">
      {yogas.map((y, i) => (
        <div key={i} className="bg-slate-800/60 border border-slate-700 rounded p-2 text-[11px]">
          <div className="font-bold text-purple-300">{y.name}{y.strength && <span className="ml-2 text-[9px] text-white/40">[{y.strength}]</span>}</div>
          {y.description && <div className="text-white/70 mt-0.5">{y.description}</div>}
        </div>
      ))}
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-8">
      <div className="text-white/60">{children}</div>
    </div>
  );
}

export default AstroLLMPage;
